import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { ApplicationCachedData, CountryDetails, User, ArticleData, ConsultationDetailData, LocationData } from '../interfaces/IApplicationData';
import { logout, logoutAndClearCache, switchRoleAndClearCache } from '../action/logout-action';
import { limitCacheSize, cleanupConsultationCache, cleanupProfileCache, cleanupLocationCache } from '../../shared-lib/utils/cache-utils';

const initialState: ApplicationCachedData = {
  countries: [],
  states: [],
  cities: [],
  categories: [],
  skills: [],
  consultants: {},
  articles: {},
  consultationDetails: {},
  profileData: {},
  locationData: {}
}

export const applicationDataSlice = createSlice({
  name: 'applicationData',
  initialState,
  reducers: { 

    storeCountries: (state=initialState, action: PayloadAction<CountryDetails[]>) => {  
      state.countries = action?.payload;
    },

    storeStates: (state=initialState, action: PayloadAction<any[]>) => {
      state.states = action?.payload;
    },

    storeCities: (state=initialState, action: PayloadAction<any[]>) => {
      state.cities = action?.payload;
    },

    storeCategories: (state=initialState, action: PayloadAction<any[]>) => {
      state.categories = action?.payload;
    },

    storeSkills: (state=initialState, action: PayloadAction<any[]>) => {
      state.skills = action?.payload;
    },

    storeCounsultants: (state=initialState, action: PayloadAction<User>) => {
      state.consultants = {
        ...state.consultants , 
        [action.payload.userDetails.userId]: action.payload 
      }
    },

    storeArticle: (state=initialState, action: PayloadAction<ArticleData>) => {
      state.articles = {
        ...state.articles,
        [action.payload.id]: action.payload
      };
    },

    storeConsultationDetail: (state=initialState, action: PayloadAction<{ id: string; data: ConsultationDetailData }>) => {
      // Ensure consultationDetails is initialized
      if (!state.consultationDetails) {
        state.consultationDetails = {};
      }
      
      state.consultationDetails = {
        ...state.consultationDetails,
        [action.payload.id]: action.payload.data
      };
      
      // Limit cache size to prevent localStorage from growing too large
      state.consultationDetails = limitCacheSize(state.consultationDetails, 50);
    },

    storeLocationData: (state=initialState, action: PayloadAction<{ key: string; data: LocationData }>) => {
      // Ensure locationData is initialized
      if (!state.locationData) {
        state.locationData = {};
      }
      
      state.locationData[action.payload.key] = action.payload.data;
    },

    storeProfileData: (state=initialState, action: PayloadAction<{ userId: string; data: any }>) => {
      // Ensure profileData is initialized
      if (!state.profileData) {
        state.profileData = {};
      }
      
      state.profileData[action.payload.userId] = action.payload.data;
      
      // Limit cache size to prevent localStorage from growing too large
      state.profileData = limitCacheSize(state.profileData, 20);
    },

    removeLocationData: (state=initialState, action: PayloadAction<string>) => {
      if (state.locationData && state.locationData[action.payload]) {
        delete state.locationData[action.payload];
      }
    },

    cleanupExpiredCache: (state) => {
      // Clean up expired consultation details
      if (state.consultationDetails) {
        state.consultationDetails = cleanupConsultationCache(state.consultationDetails);
      }
      
      // Clean up expired profile data
      if (state.profileData) {
        state.profileData = cleanupProfileCache(state.profileData);
      }
      
      // Clean up expired location data
      if (state.locationData) {
        state.locationData = cleanupLocationCache(state.locationData);
      }
    },

    clearAllCachedData: (state) => {
      state.countries = [];
      state.states = [];
      state.cities = [];
      state.categories = [];
      state.skills = [];
      state.consultants = {};
      state.articles = {};
      state.consultationDetails = {};
      state.profileData = {};
      state.locationData = {};
    },

    // Force refresh all cached data (clears cache but keeps structure)
    forceRefreshCache: (state) => {
      // Clear all cached data to force fresh API calls
      state.countries = [];
      state.states = [];
      state.cities = [];
      state.categories = [];
      state.skills = [];
      state.consultants = {};
      state.articles = {};
      state.consultationDetails = {};
      state.profileData = {};
      state.locationData = {};
    }
 
  },
  extraReducers: (builder) => {
    builder
      .addCase(logout, (state) => {
        // Clear all cached data on logout
        state.countries = [];
        state.states = [];
        state.cities = [];
        state.categories = [];
        state.skills = [];
        state.consultants = {};
        state.articles = {};
        state.consultationDetails = {};
        state.profileData = {};
        state.locationData = {};
      })
      .addCase(logoutAndClearCache, (state) => {
        // Enhanced logout that clears everything
        state.countries = [];
        state.states = [];
        state.cities = [];
        state.categories = [];
        state.skills = [];
        state.consultants = {};
        state.articles = {};
        state.consultationDetails = {};
        state.profileData = {};
        state.locationData = {};
      })
      .addCase(switchRoleAndClearCache, (state) => {
        // Clear role-scoped cache; keep location masters (countries/states/cities)
        // so setup pages like professional-info can finish loading after Switch Now.
        state.categories = [];
        state.skills = [];
        state.consultants = {};
        state.articles = {};
        state.consultationDetails = {};
        state.profileData = {};
        state.locationData = {};
      });
  },
});

export const { 
  storeCountries, 
  storeStates, 
  storeCities, 
  storeCategories, 
  storeSkills, 
  storeCounsultants, 
  storeArticle, 
  storeConsultationDetail, 
  storeLocationData, 
  storeProfileData,
  removeLocationData, 
  cleanupExpiredCache, 
  clearAllCachedData,
  forceRefreshCache
} = applicationDataSlice.actions;

// Helper functions for cache management
export const GetCachedArticles = (articleID: string) =>
  (state: any) => state.applicationDataSlice.articles[articleID];

export const GetCachedConsultationDetails = (consultationId: string) =>
  (state: any) => state.applicationDataSlice.consultationDetails?.[consultationId];

export const GetCachedProfileData = (userId: string) =>
  (state: any) => state.applicationDataSlice.profileData?.[userId];

export const GetCachedLocationData = (key: string) =>
  (state: any) => state.applicationDataSlice.locationData?.[key];

export default applicationDataSlice.reducer;